/**
 * caseCounts: the ONE formula behind every case count the reader sees.
 *
 * Two surfaces state a number about the same list, roughly 60px apart: the
 * Building page's tab strip ("Case · 1 open", `buildingTabCounts` in
 * `pages/Portal/BuildingDetail/tabs.ts`) and the case board's header ("Managed
 * cases · 1 open", `BuildingCasesSection`). As two expressions,
 * an open-status test in one file and `cases.length` in the other, they
 * agree only until something is resolved, then render "Case 1 open" directly
 * above "Managed cases · 2" with nothing saying which is wrong.
 *
 * It lives under `components/portal`, not in `tabs.ts`, because a page may
 * import the component layer and not the reverse. `tabs.ts` re-exports
 * `openCaseCount` so the tab contract reads in one file, and the route entry
 * pays nothing: this module imports nothing. The sidebar rail reads it too.
 */

export const CASE_RESOLVED = "RESOLVED";

/** A case, as far as any count cares. */
export interface CountableCase {
  status: string;
}

/**
 * How many of these cases are LIVE.
 *
 * `undefined` in, `undefined` out, the whole reason this is a function and not
 * a `.filter().length` at two call sites. The cases query 404s while the
 * per-company flag is off, and errors transiently; both land as "no payload",
 * which is not the fact "this building has no open case" and does not get to
 * render as one.
 */
export const openCaseCount = (
  cases: readonly CountableCase[] | null | undefined,
): number | undefined =>
  cases == null
    ? undefined
    : cases.filter((c) => c.status !== CASE_RESOLVED).length;

/**
 * How many have been closed out. Same payload, stated separately so a board
 * listing four blocks of which one is resolved can say so, instead of printing
 * one number matching neither the strip above nor the blocks below.
 */
export const resolvedCaseCount = (
  cases: readonly CountableCase[] | null | undefined,
): number | undefined =>
  cases == null
    ? undefined
    : cases.filter((c) => c.status === CASE_RESOLVED).length;

/** A case, as far as the matter→case index cares. Structural for the same
 *  reason `CountableCase` is: `tabs.ts` imports this module eagerly on the
 *  route, and naming `ViolationCase` would drag `queries/`, and axios with it,
 *  into that chunk to read two string fields. */
export interface IndexableCase {
  caseId: string;
  spine: { tickets: readonly { matterId: string }[] };
}

/**
 * Matter id → the case that covers it.
 *
 * TWO SURFACES ASK IT: the Exposure board's BY PATTERN table (stamp a pattern
 * MANAGED CASE) and the Record tab's ledger drawer. Re-typing the shape is how a
 * copy ends up keyed on `ticketNumber` instead of `matterId`. Both call this; a
 * `grep` for `spine.tickets` outside this file and the queries layer is empty.
 *
 * `undefined` in → an EMPTY index, not `undefined`: every caller asks "does
 * THIS matter have a case", and a missing payload means "none known", which an
 * empty map states correctly at every lookup. The no-payload ≠ no-cases
 * distinction the counts above preserve matters for a number on screen, not for
 * a per-row lookup that renders nothing either way.
 */
export const buildCaseIdByMatterId = (
  cases: readonly IndexableCase[] | null | undefined,
): Record<string, string> => {
  const out: Record<string, string> = {};
  (cases ?? []).forEach((c) => {
    c.spine.tickets.forEach((t) => {
      out[t.matterId] = c.caseId;
    });
  });
  return out;
};
